// Client-side photo-consent plumbing shared by every upload UI. Pure (no React), so the gate is unit-tested.
// The server shape (R3) is fixed by lib/ethics/photo-consent-server.ts:
//   multipart: form field photoConsent=<version>; JSON: photoConsent: { version }.
import { PHOTO_CONSENT_FIELD, consentBody } from '@/lib/ethics/photo-consent';

/** Adds photoConsent=<version> to a multipart body when the parent ticked the box. */
export function withPhotoConsent(fd: FormData, version: string | null | undefined): FormData {
  if (version) fd.set(PHOTO_CONSENT_FIELD, version);
  return fd;
}

/** `{ photoConsent: { version } }` to spread into a JSON body, or nothing when there is no tick. */
export function photoConsentJson(version: string | null | undefined): { photoConsent?: { version: string } } {
  return version ? consentBody(version) : {};
}

/**
 * Holds a picked file until the box is ticked. pick() with the box unticked does NOT upload: it keeps the file
 * and calls blocked(); ticking (consentChanged with a version) then continues with that file.
 */
export interface ConsentGate<F> {
  pick(file: F): boolean;
  /** Hold a file without the blocked() error (e.g. the free-cover photo of a cover that has no stored consent). */
  hold(file: F): void;
  consentChanged(version: string | null): void;
  pending(): F | null;
  clear(): void;
}

export function createConsentGate<F>(o: {
  consent: () => string | null;
  /** False when no tick is needed (e.g. the free-cover photo, whose consent the server inherits). */
  required?: () => boolean;
  proceed: (file: F, version: string | null) => void;
  blocked: (file: F) => void;
}): ConsentGate<F> {
  let held: F | null = null;
  return {
    pick(file) {
      const version = o.consent();
      if ((o.required ? o.required() : true) && !version) {
        held = file;
        o.blocked(file);
        return false;
      }
      held = null;
      o.proceed(file, version);
      return true;
    },
    hold(file) {
      held = file;
    },
    consentChanged(version) {
      if (!version || held === null) return;
      const file = held;
      held = null;
      o.proceed(file, version);
    },
    pending: () => held,
    clear() {
      held = null;
    },
  };
}
